import React from 'react';
import {flexRender} from '@tanstack/react-table';
import classnames from 'classnames';

import Text from '../../../atoms/Text/Text.react';
import Checkbox from '../../../atoms/Checkbox/Checkbox.react';
import IconButton from '../../../molecules/IconButton/IconButton.react';

import './data-table.scss';
import type {DataTableHeaderProps} from './DataTable.types';

/**
 * Renders the `<thead>` for DataTable.
 *
 * Re-uses the existing `o-table__header*` CSS classes so the visual output
 * matches the legacy atomic Table exactly.
 */
function DataTableHeader<TData>({
  table,
  headerless,
  enableRowSelection
}: DataTableHeaderProps<TData>) {
  if (headerless) {
    return null;
  }

  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr className='o-table__row' key={headerGroup.id}>
          {/* Select-all checkbox */}
          {enableRowSelection && (
            <th className='o-table__header o-table__selection-header'>
              <Checkbox
                label='Select all rows'
                className='o-table__row-select'
                checked={table.getIsAllRowsSelected()}
                onClick={table.getToggleAllRowsSelectedHandler()}
                size='s'
              />
            </th>
          )}

          {headerGroup.headers.map((header) => {
            const canSort = header.column.getCanSort();
            const sortDirection = header.column.getIsSorted(); // 'asc' | 'desc' | false
            const align = (header.column.columnDef.meta as any)?.align;
            const isActionMenu = (header.column.columnDef.meta as any)?.isActionMenu;
            const headerClassName = (header.column.columnDef.meta as any)?.className;

            return (
              <th
                key={header.id}
                className={classnames('o-table__header', headerClassName, {
                  [`o-table__header--${align}`]: align,
                  'o-table__action-menu-header': isActionMenu
                })}
                style={{
                  ...(header.getSize() !== 150 ? {width: header.getSize()} : {}),
                  ...(header.column.columnDef.minSize
                    ? {minWidth: header.column.columnDef.minSize}
                    : {})
                }}
              >
                <div
                  className={classnames('o-table__header-content', {
                    'flex justify-between': canSort
                  })}
                >
                  {header.isPlaceholder
                    ? null
                    : (() => {
                        const rendered = flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        );
                        return typeof rendered === 'string' ? (
                          <Text className='o-table__header-text' size='m' bold>
                            {rendered}
                          </Text>
                        ) : (
                          rendered
                        );
                      })()}
                  {canSort && (
                    <IconButton
                      color={sortDirection ? 'primary' : 'secondary'}
                      className='o-table__header-sort'
                      label='sort'
                      size='s'
                      icon={`arrow-${sortDirection === 'desc' ? 'down' : 'up'}`}
                      onClick={header.column.getToggleSortingHandler()}
                    />
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

export default DataTableHeader;
